import { useMutation, useQueryClient } from 'react-query';
import { IdResponse } from '../../../../../../../types/API';
import { InsurancePolicyPackage } from '../../../../../../advisory-report/src/types/AdvisoryReport';
import { wgApi } from '../../../../../../app/src/features/api/wgApi';
import {
  getUseInsurancePolicyPackagesQKey,
  InsurencePolicyPackagesPartialResponse,
} from './getInsurancePolicyPackagesByParty';
import { getUseInsurancePolicyPackageQkey } from './getInsurancePolicyPackageById';

interface Params {
  partyId: string;
  insurancePackage: Partial<InsurancePolicyPackage>;
}

export const putInsurancePolicyPackage = async ({
  partyId,
  insurancePackage,
}: Params) => {
  const { data, isError } = await wgApi.put<IdResponse>(
    `/v1/api/parties/${partyId}/policy-packages`,
    insurancePackage,
  );
  if (isError) throw new Error(JSON.stringify(data));
  return data;
};

export const useUpdateInsurancePolicyPackage = () => {
  const queryClient = useQueryClient();

  return useMutation(putInsurancePolicyPackage, {
    onMutate: async ({ partyId, insurancePackage }) => {
      const policyPackagesQkey = getUseInsurancePolicyPackagesQKey({
        partyId,
      });

      const singlePolicyPackageQkey = getUseInsurancePolicyPackageQkey({
        partyId,
        packageId: insurancePackage.id || '',
      });
      await queryClient.cancelQueries(singlePolicyPackageQkey);
      const previousSinglePackage = queryClient.getQueryData(
        singlePolicyPackageQkey,
      );
      queryClient.setQueryData(singlePolicyPackageQkey, insurancePackage);

      queryClient.setQueryData(singlePolicyPackageQkey, insurancePackage);

      // packages call
      await queryClient.cancelQueries(policyPackagesQkey);

      const previousPackages = queryClient.getQueryData(policyPackagesQkey);

      queryClient.setQueryData(
        policyPackagesQkey,
        (prev: InsurencePolicyPackagesPartialResponse | undefined) => {
          const index = prev?.items.findIndex(
            ({ id }) => id === insurancePackage.id,
          );
          if (index != null && index > -1) {
            const newPackages = [...(prev?.items || [])];
            newPackages[index] = insurancePackage;
            return {
              ...prev,
              items: newPackages,
            };
          }
          return {
            ...prev,
            items: [...(prev?.items || []), insurancePackage],
          };
        },
      );

      return { previousPackages, previousSinglePackage };
    },
    onError: (_, { partyId, insurancePackage }, ctx) => {
      const policyPackagesQkey = getUseInsurancePolicyPackagesQKey({
        partyId,
      });
      const singlePolicyPackageQkey = getUseInsurancePolicyPackageQkey({
        partyId,
        packageId: insurancePackage.id || '',
      });
      queryClient.setQueryData(policyPackagesQkey, ctx?.previousPackages);
      queryClient.setQueryData(
        singlePolicyPackageQkey,
        ctx?.previousSinglePackage,
      );
    },
    onSuccess: (_, { partyId, insurancePackage }) => {
      if (insurancePackage.id) {
        queryClient.invalidateQueries(
          getUseInsurancePolicyPackageQkey({
            partyId,
            packageId: insurancePackage.id,
          }),
        );
      }
      queryClient.invalidateQueries(
        getUseInsurancePolicyPackagesQKey({ partyId }),
      );
    },
  });
};
